import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import PanelValidacion from '../components/common/PanelValidacion';
import GaleriaFotos from '../components/common/GaleriaFotos';
import { inspeccionesService, sesionActual, descargarReportePDF, descargarReporteExcel } from '../services/inspecciones';
import toast from 'react-hot-toast';
import {
    ClipboardCheck,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    Clock,
    AlertCircle,
    Plus,
    X,
    Trash2,
    Pencil,
    MessageSquare,
    Download,
    FileSpreadsheet,
    History,
} from 'lucide-react';

// ─── Badge de estado ─────────────────────────────────────────────────────────

const EstadoBadge = ({ estado }) => {
    const config = {
        ok: { icon: CheckCircle2, label: 'OK', cls: 'bg-green-100 text-green-700' },
        pendiente: { icon: Clock, label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700' },
        observado: { icon: AlertCircle, label: 'Observado', cls: 'bg-red-100 text-red-700' },
    };
    const { icon: Icon, label, cls } = config[estado] || config.pendiente;
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>
            <Icon className="h-3.5 w-3.5 mr-1" />
            {label}
        </span>
    );
};

// ─── Accordion de pregunta ────────────────────────────────────────────────────

const PreguntaAccordion = ({ item, sesion, onOk, onResponder, onEliminar, esSesionHistorica = false }) => {
    const [abierto, setAbierto] = useState(false);
    const [textRespuesta, setTextRespuesta] = useState(item.ultima_respuesta?.respuesta || '');
    const [guardando, setGuardando] = useState(false);
    const [marcandoOk, setMarcandoOk] = useState(false);

    const estado = item.ultima_respuesta?.estado || 'pendiente';

    const handleOk = async (e) => {
        e.stopPropagation();
        setMarcandoOk(true);
        await onOk(item.id);
        setMarcandoOk(false);
    };

    const handleGuardarRespuesta = async () => {
        setGuardando(true);
        await onResponder(item.id, textRespuesta, 'ok');
        setGuardando(false);
    };

    return (
        <div className={`border rounded-xl overflow-hidden mb-3 transition-all ${
            estado === 'ok' ? 'border-green-200' : 'border-gray-200'
        }`}>
            {/* Cabecera */}
            <div
                className={`flex items-center justify-between px-5 py-4 cursor-pointer transition-colors ${
                    estado === 'ok' ? 'bg-green-50 hover:bg-green-100' : 'bg-white hover:bg-gray-50'
                }`}
                onClick={() => setAbierto(!abierto)}
            >
                {/* Texto pregunta */}
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-800 truncate">
                        {item.texto}
                    </span>
                </div>

                {/* Controles derecha */}
                <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
                    <EstadoBadge estado={estado} />

                    {/* Botón OK rápido */}
                    {estado !== 'ok' && !esSesionHistorica && (
                        <button
                            onClick={handleOk}
                            disabled={marcandoOk}
                            className="flex items-center px-3 py-1.5 bg-green-500 text-white text-xs font-semibold rounded-lg hover:bg-green-600 transition-colors disabled:opacity-60"
                        >
                            {marcandoOk ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-1"></div>
                            ) : (
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                            )}
                            OK
                        </button>
                    )}

                    {!esSesionHistorica && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onEliminar(item.id); }}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar pregunta"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}

                    {abierto ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                </div>
            </div>

            {/* Contenido expandido */}
            {abierto && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    {item.descripcion && (
                        <p className="text-sm text-gray-500 mb-4 italic">{item.descripcion}</p>
                    )}

                    {/* Respuesta */}
                    <div className="space-y-3">
                        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            <MessageSquare className="h-3.5 w-3.5 inline mr-1" />
                            Observaciones / Respuesta
                        </label>
                        <textarea
                            rows={3}
                            value={textRespuesta}
                            onChange={(e) => setTextRespuesta(e.target.value)}
                            placeholder="Escribe observaciones o detalles..."
                            readOnly={esSesionHistorica}
                            className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                esSesionHistorica ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                            }`}
                        />

                        {/* Selector de estado */}
                        <div className="flex items-center space-x-3">
                            {['ok', 'observado', 'pendiente'].map((est) => (
                                <button
                                    key={est}
                                    onClick={() => onResponder(item.id, textRespuesta, est)}
                                    disabled={esSesionHistorica}
                                    className={`flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                        estado === est
                                            ? est === 'ok' ? 'bg-green-500 text-white border-green-500'
                                              : est === 'observado' ? 'bg-red-500 text-white border-red-500'
                                              : 'bg-yellow-500 text-white border-yellow-500'
                                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {est === 'ok' && <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                                    {est === 'observado' && <AlertCircle className="h-3.5 w-3.5 mr-1" />}
                                    {est === 'pendiente' && <Clock className="h-3.5 w-3.5 mr-1" />}
                                    {est.charAt(0).toUpperCase() + est.slice(1)}
                                </button>
                            ))}

                            {!esSesionHistorica && (
                                <button
                                    onClick={handleGuardarRespuesta}
                                    disabled={guardando}
                                    className="ml-auto px-4 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
                                >
                                    {guardando ? 'Guardando...' : 'Guardar'}
                                </button>
                            )}
                        </div>

                        {item.ultima_respuesta && (
                            <p className="text-xs text-gray-400">
                                Respondido por: {item.ultima_respuesta.respondido_por || 'N/A'} —{' '}
                                {item.ultima_respuesta.fecha_respuesta
                                    ? new Date(item.ultima_respuesta.fecha_respuesta).toLocaleString('es-PE')
                                    : ''}
                            </p>
                        )}

                        <GaleriaFotos modulo="inspeccion_quincenal" itemId={item.id} sesion={sesion} soloLectura={esSesionHistorica} />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Modal Agregar Pregunta ───────────────────────────────────────────────────

const ModalAgregarPregunta = ({ onCerrar, onGuardar }) => {
    const [texto, setTexto] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [guardando, setGuardando] = useState(false);

    const handleGuardar = async () => {
        if (!texto.trim()) { toast.error('El texto de la pregunta es obligatorio'); return; }
        setGuardando(true);
        await onGuardar(texto.trim(), descripcion.trim() || null);
        setGuardando(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center space-x-2">
                        <Plus className="h-5 w-5 text-blue-600" />
                        <h2 className="text-lg font-bold text-gray-900">Agregar Pregunta</h2>
                    </div>
                    <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Pregunta <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={texto}
                            onChange={(e) => setTexto(e.target.value)}
                            placeholder="Ej: ¿Se revisaron los extintores?"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleGuardar()}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Descripción / Guía <span className="text-gray-400 font-normal">(opcional)</span>
                        </label>
                        <textarea
                            rows={3}
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            placeholder="Detalle adicional para quien responde..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="flex justify-end space-x-3 px-6 pb-6">
                    <button
                        onClick={onCerrar}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleGuardar}
                        disabled={guardando || !texto.trim()}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
                    >
                        {guardando ? 'Guardando...' : 'Agregar Pregunta'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Modal Enviar Reporte por Email ──────────────────────────────────────────

// ─── Página principal ─────────────────────────────────────────────────────────

const InspeccionesPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [checklist, setChecklist] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [mostrarModal, setMostrarModal] = useState(false);
    const [descargando, setDescargando] = useState(false);
    const [descargandoExcel, setDescargandoExcel] = useState(false);

    const sesionParam = searchParams.get('sesion');
    const sesion = sesionParam || sesionActual();
    const esSesionHistorica = !!sesionParam && sesionParam !== sesionActual();

    const totalOk = checklist.filter((i) => i.ultima_respuesta?.estado === 'ok').length;
    const totalPendiente = checklist.filter((i) => !i.ultima_respuesta || i.ultima_respuesta.estado === 'pendiente').length;
    const totalObservado = checklist.filter((i) => i.ultima_respuesta?.estado === 'observado').length;

    const cargar = async () => {
        setCargando(true);
        try {
            const data = await inspeccionesService.checklist(sesion);
            setChecklist(data);
        } catch {
            toast.error('Error al cargar el checklist');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, [sesion]);

    const handleOk = async (id) => {
        try {
            const actualizada = await inspeccionesService.marcarOk(id, sesion);
            setChecklist((prev) =>
                prev.map((item) =>
                    item.id === id ? { ...item, ultima_respuesta: actualizada } : item
                )
            );
        } catch {
            toast.error('Error al marcar OK');
        }
    };

    const handleResponder = async (id, respuesta, estado) => {
        try {
            const actualizada = await inspeccionesService.responder(id, respuesta, estado, sesion);
            setChecklist((prev) =>
                prev.map((item) =>
                    item.id === id ? { ...item, ultima_respuesta: actualizada } : item
                )
            );
            toast.success('Respuesta guardada');
        } catch {
            toast.error('Error al guardar respuesta');
        }
    };

    const handleEliminar = async (id) => {
        if (!window.confirm('¿Eliminar esta pregunta del checklist?')) return;
        try {
            await inspeccionesService.eliminarPregunta(id);
            setChecklist((prev) => prev.filter((item) => item.id !== id));
            toast.success('Pregunta eliminada');
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const handleAgregarPregunta = async (texto, descripcion) => {
        try {
            await inspeccionesService.crearPregunta(texto, descripcion, checklist.length);
            setMostrarModal(false);
            toast.success('Pregunta agregada');
            cargar();
        } catch {
            toast.error('Error al agregar pregunta');
        }
    };

    const handleDescargarPDF = async () => {
        setDescargando(true);
        try {
            await descargarReportePDF(sesion);
            toast.success('Reporte descargado');
        } catch {
            toast.error('Error al descargar el reporte');
        } finally {
            setDescargando(false);
        }
    };

    const handleDescargarExcel = async () => {
        setDescargandoExcel(true);
        try {
            await descargarReporteExcel(sesion);
            toast.success('Excel descargado');
        } catch {
            toast.error('Error al descargar el Excel');
        } finally {
            setDescargandoExcel(false);
        }
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                            <ClipboardCheck className="h-7 w-7 mr-2 text-blue-600" />
                            Inspecciones Quincenales
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Sesión: <span className="font-semibold text-blue-600">{sesion}</span>
                        </p>
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => navigate('/inspecciones/historial')}
                            className="flex items-center px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm shadow-sm"
                        >
                            <History className="h-4 w-4 mr-2" />
                            Historial
                        </button>
                        <button
                            onClick={handleDescargarPDF}
                            disabled={descargando}
                            className="flex items-center px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-60 transition-colors font-semibold text-sm shadow-sm"
                        >
                            {descargando
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <Download className="h-4 w-4 mr-2" />
                            }
                            {descargando ? 'Descargando...' : 'Descargar PDF'}
                        </button>
                        <button
                            onClick={handleDescargarExcel}
                            disabled={descargandoExcel}
                            className="flex items-center px-4 py-2.5 bg-emerald-700 text-white rounded-xl hover:bg-emerald-800 disabled:opacity-60 transition-colors font-semibold text-sm shadow-sm"
                        >
                            {descargandoExcel
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <FileSpreadsheet className="h-4 w-4 mr-2" />
                            }
                            {descargandoExcel ? 'Descargando...' : 'Descargar Excel'}
                        </button>
                        {!esSesionHistorica && (
                            <button
                                onClick={() => setMostrarModal(true)}
                                className="flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm shadow-sm"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Agregar Pregunta
                            </button>
                        )}
                    </div>
                </div>

                <PanelValidacion modulo="inspeccion_quincenal" sesion={sesion} />

                {/* Banner sesión histórica */}
                {esSesionHistorica && (
                    <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-center gap-2 text-amber-800 text-sm">
                            <History className="h-4 w-4" />
                            <span>Estás viendo una sesión <strong>histórica</strong>. Las respuestas no se modifican aquí.</span>
                        </div>
                        <button
                            onClick={() => navigate('/inspecciones')}
                            className="text-amber-800 text-xs font-semibold underline hover:no-underline"
                        >
                            Ir a la sesión actual
                        </button>
                    </div>
                )}

                {/* Resumen de progreso */}
                {checklist.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                            <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-green-700">{totalOk}</p>
                            <p className="text-xs text-green-600">OK</p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                            <Clock className="h-6 w-6 text-yellow-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-yellow-700">{totalPendiente}</p>
                            <p className="text-xs text-yellow-600">Pendiente</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-red-700">{totalObservado}</p>
                            <p className="text-xs text-red-600">Observado</p>
                        </div>
                    </div>
                )}

                {/* Barra de progreso */}
                {checklist.length > 0 && (
                    <div className="mb-6">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Progreso de la quincena</span>
                            <span>{totalOk}/{checklist.length} completadas</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${checklist.length > 0 ? (totalOk / checklist.length) * 100 : 0}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Lista de preguntas */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-700 mb-4">
                        Checklist — {checklist.length} pregunta{checklist.length !== 1 ? 's' : ''}
                    </h2>

                    {cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {!cargando && checklist.length === 0 && (
                        <div className="text-center py-14 text-gray-400">
                            <ClipboardCheck className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">No hay preguntas en el checklist.</p>
                            <p className="text-xs mt-1">Usa el botón "Agregar Pregunta" para comenzar.</p>
                        </div>
                    )}

                    {!cargando && checklist.map((item) => (
                        <PreguntaAccordion
                            key={item.id}
                            item={item}
                            sesion={sesion}
                            onOk={handleOk}
                            onResponder={handleResponder}
                            onEliminar={handleEliminar}
                            esSesionHistorica={esSesionHistorica}
                        />
                    ))}
                </div>
            </div>

            {/* Modal agregar pregunta */}
            {mostrarModal && (
                <ModalAgregarPregunta
                    onCerrar={() => setMostrarModal(false)}
                    onGuardar={handleAgregarPregunta}
                />
            )}

        </Layout>
    );
};

export default InspeccionesPage;
